import React, { useContext, useState } from 'react';
import { ReloadOutlined } from '@ant-design/icons';
import { Alert, Badge, Button, Select, Space, Tooltip } from 'antd';

import ApprovalsTab from '@totem/components/maintenanceWindowsV2/approvals/ApprovalsTab';
import MaintenanceWindowsV2Context from '@totem/components/maintenanceWindowsV2/MaintenanceWindowsV2Context';
import SummaryTab from '@totem/components/maintenanceWindowsV2/summary/SummaryTab';
import { isPendingForUser } from '@totem/components/maintenanceWindowsV2/utilities';
import WindowsTab, {
  WindowsView,
} from '@totem/components/maintenanceWindowsV2/windows/WindowsTab';
import BypassOverlayNetworksPanel from '@totem/components/portfolio/network/overlayBypass/BypassOverlayNetworksPanel';
import IBCard from '@totem/components/ui_components/antDesignOverrides/IBCard';
import IBTabs from '@totem/components/ui_components/antDesignOverrides/IBTabs';
import OrganizationContext from '@totem/components/userContext/organizationContext/organizationContext';
import { permissions } from '@totem/components/userContext/security/permissions/permissions';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';

const LOOKBACK_OPTIONS = [
  { value: 30, label: 'History: 30 days' },
  { value: 90, label: 'History: 90 days' },
  { value: 180, label: 'History: 180 days' },
  { value: 365, label: 'History: 1 year' },
];

const filterOption = (
  search: string,
  option?: { label: string; value: string },
) => (option?.label ?? '').toLowerCase().includes(search.toLowerCase());

export const MaintenanceWindowsV2Extra = () => {
  const { filters, setFilters, loading, onAction } = useContext(
    MaintenanceWindowsV2Context,
  );
  const { regions, buildings, controlSystems } =
    useContext(OrganizationContext);

  const regionOptions = (regions ?? []).map((region) => ({
    value: region.id,
    label: region.name,
  }));
  // With regions selected, only offer those regions' buildings (a region's children are its buildings).
  const regionBuildingIds = new Set(
    (regions ?? [])
      .filter((region) => filters.regionIds.includes(region.id))
      .flatMap((region) => (region.children ?? []).map((child) => child.id)),
  );
  const visibleBuildings = (buildings ?? []).filter(
    (building) =>
      filters.regionIds.length === 0 || regionBuildingIds.has(building.id),
  );
  const buildingOptions = visibleBuildings.map((building) => ({
    value: building.id,
    label: building.name,
  }));
  // Likewise, a building's children are its control systems.
  const selectedBuildingIds = filters.buildingIds.length
    ? new Set(filters.buildingIds)
    : new Set(visibleBuildings.map((building) => building.id));
  const buildingControlSystemIds = new Set(
    (buildings ?? [])
      .filter((building) => selectedBuildingIds.has(building.id))
      .flatMap((building) =>
        (building.children ?? []).map((child) => child.id),
      ),
  );
  const controlSystemOptions = (controlSystems ?? [])
    .filter(
      (controlSystem) =>
        (filters.regionIds.length === 0 && filters.buildingIds.length === 0) ||
        buildingControlSystemIds.has(controlSystem.id),
    )
    .map((controlSystem) => ({
      value: controlSystem.id,
      label: controlSystem.name,
    }));

  return (
    <Space wrap>
      <Select
        mode="multiple"
        style={{ minWidth: 150, maxWidth: 300 }}
        options={regionOptions}
        value={filters.regionIds}
        onChange={(values: string[]) => setFilters({ regionIds: values })}
        placeholder="All regions"
        showSearch
        filterOption={filterOption}
        maxTagCount="responsive"
      />
      <Select
        mode="multiple"
        style={{ minWidth: 170, maxWidth: 340 }}
        options={buildingOptions}
        value={filters.buildingIds}
        onChange={(values: string[]) => setFilters({ buildingIds: values })}
        placeholder="All buildings"
        showSearch
        filterOption={filterOption}
        maxTagCount="responsive"
      />
      <Select
        mode="multiple"
        style={{ minWidth: 170, maxWidth: 340 }}
        options={controlSystemOptions}
        value={filters.controlSystemIds}
        onChange={(values: string[]) =>
          setFilters({ controlSystemIds: values })
        }
        placeholder="All control systems"
        showSearch
        filterOption={filterOption}
        maxTagCount="responsive"
      />
      <Select
        style={{ minWidth: 150 }}
        options={LOOKBACK_OPTIONS}
        value={filters.lookbackDays}
        onChange={(value: number) => setFilters({ lookbackDays: value })}
      />
      <Tooltip title="Refresh">
        <Button
          icon={<ReloadOutlined />}
          loading={loading}
          onClick={() => onAction('refresh', null)}
          aria-label="Refresh"
        />
      </Tooltip>
    </Space>
  );
};

const MaintenanceWindowsV2Layout = () => {
  const { failed, data, now } = useContext(MaintenanceWindowsV2Context);
  const { userProfile } = useContext(UserProfileContext);
  const canSeeBypassOverlays = permissions.internal.hasPermissions(userProfile);
  const [activeTab, setActiveTab] = useState<string>('summary');
  const [windowsView, setWindowsView] = useState<WindowsView>('current');

  const myPending = data.filter((win) =>
    isPendingForUser(win, userProfile.email, now),
  ).length;

  const handleDrillDown = (view: WindowsView) => {
    setWindowsView(view);

    setActiveTab('windows');
  };

  return (
    <IBCard
      isCovered
      title="Maintenance Windows"
      extra={<MaintenanceWindowsV2Extra />}
    >
      {failed && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 12 }}
          title="Maintenance windows could not be loaded."
        />
      )}
      <IBTabs
        activeKey={activeTab}
        onChange={setActiveTab}
        items={[
          {
            key: 'summary',
            label: 'Summary',
            children: <SummaryTab onDrillDown={handleDrillDown} />,
          },
          {
            key: 'windows',
            label: 'Windows',
            children: (
              <WindowsTab view={windowsView} onViewChange={setWindowsView} />
            ),
          },
          {
            key: 'approvals',
            label: (
              <Space size={6}>
                Approvals
                {myPending > 0 && <Badge count={myPending} color="orange" />}
              </Space>
            ),
            children: <ApprovalsTab />,
          },
          // Live from the Conductor, so it only loads when opened; IB users only (the API enforces this too).
          ...(canSeeBypassOverlays
            ? [
                {
                  key: 'bypassOverlays',
                  label: 'Bypass overlays',
                  children: <BypassOverlayNetworksPanel />,
                },
              ]
            : []),
        ]}
      />
    </IBCard>
  );
};

export default MaintenanceWindowsV2Layout;
